

import { useState, useEffect } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import empty from "../../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faQuestionCircle,
    faPlus,
    faEye,
    faEdit,
    faTrash,
    faCheck,
    faTimes,
    faSearch,
    faArrowLeft,
    faLanguage,
} from "@fortawesome/free-solid-svg-icons"

export default function FreeTestQuestions() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [testQuestions, setTestQuestions] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingTest, setLoadingTest] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("")
    const [language, setLanguage] = useState("english")
    const [testDetails, setTestDetails] = useState(null)
    const [subjectFilter, setSubjectFilter] = useState("")
    const [subjects, setSubjects] = useState([])
    const [topics, setTopics] = useState([])
    const [loadingSubjects, setLoadingSubjects] = useState(false)
    const [loadingTopics, setLoadingTopics] = useState(false)
    useEffect(() => {
        fetchTestQuestions()
    }, [id])

    useEffect(() => {
        fetchSubjects()
        fetchTopics()
    }, [testQuestions])

    const fetchSubjects = async () => {
        try {
            setLoadingSubjects(true)
            const response = await axios.get(`${baseUrl}/subject/list`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.data.status && Array.isArray(response.data.data)) {
                setSubjects(response.data.data)
            } else {
                console.error("Error fetching subjects:", response.data)
                setSubjects([])
            }
        } catch (error) {
            console.error("Error fetching subjects:", error)
            setSubjects([])
        } finally {
            setLoadingSubjects(false)
        }
    }

    // Fetch topics from API
    const fetchTopics = async () => {
        try {
            setLoadingTopics(true)
            const response = await axios.get(`${baseUrl}/topic/list`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.data.status && Array.isArray(response.data.data)) {
                setTopics(response.data.data)
            } else {
                console.error("Error fetching topics:", response.data)
                setTopics([])
            }
        } catch (error) {
            console.error("Error fetching topics:", error)
            setTopics([])
        } finally {
            setLoadingTopics(false)
        }
    }
    const fetchTestQuestions = async () => {
        try {
            setLoading(true)
            setLoadingTest(true)

            const response = await axios.get(
                `${baseUrl}/daily-mock-question-selected/test-questions/${id}`,
                {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                        "Content-Type": "application/json",
                    },
                },
            )

            if (response.data.status) {
                const questionsData = response.data.data || []
                setTestQuestions(questionsData)
            } else {
                Swal.fire({
                    title: "Error!",
                    text: response.data.message || "Failed to fetch test questions",
                    icon: "error",
                })
            }
        } catch (error) {
            console.error("Error fetching test questions:", error)
        } finally {
            setLoading(false)
            setLoadingTest(false)
        }
    }

    const handleStatus = async (questionId) => {
        try {
            // Update the status in our local state
            setTestQuestions((prevQuestions) =>
                prevQuestions.map((q) =>
                    q._id === questionId ? { ...q, status: q.status === "active" ? "inactive" : "active" } : q,
                ),
            )

            Swal.fire({
                title: "Status Changed!",
                text: "Question status updated successfully",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            })
        } catch (error) {
            Swal.fire({
                title: "Error!",
                text: "Failed to update status",
                icon: "error",
            })
        }
    }



    // Inside your component
    const { id: testId } = useParams();

    const handleRemoveQuestion = async (questionId) => {
        const result = await Swal.fire({
            title: "Remove Question?",
            text: "Are you sure you want to remove this question from the test?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove it!",
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${baseUrl}/daily-mock-question-selected/selected-question-delete`, {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                    body: JSON.stringify({
                        questionId,
                        testId,
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to remove question from test");
                }

                await response.json();

                // Remove question from local state
                setTestQuestions((prevQuestions) =>
                    prevQuestions.filter((q) => q._id !== questionId)
                );

                Swal.fire({
                    title: "Removed!",
                    text: "Question has been removed from the test.",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error("Error removing question:", error);
                Swal.fire({
                    title: "Error!",
                    text: "Failed to remove question from test",
                    icon: "error",
                });
            }
        }
    };


    // Helper function to get question text based on language
    const getQuestionText = (question, lang) => {
        switch (lang.toLowerCase()) {
            case "english":
                return question?.question_english || ""
            case "hindi":
                return question?.question_hindi || ""
            case "bengali":
                return question?.question_bengali || ""
            default:
                return question?.question_english || ""
        }
    }

    // Helper function to get option text based on language
    const getOptionText = (question, lang, optionKey) => {
        switch (lang.toLowerCase()) {
            case "english":
                return question?.[`option_${optionKey.toLowerCase()}_english`] || ""
            case "hindi":
                return question?.[`option_${optionKey.toUpperCase()}_hindi`] || ""
            case "bengali":
                // Bengali uses numbers (1,2,3,4) instead of letters
                const optionMap = { a: "1", b: "2", c: "3", d: "4", e: "5" }
                return question?.[`option_${optionMap[optionKey.toLowerCase()]}_bengali`] || ""
            default:
                return question?.[`option_${optionKey.toLowerCase()}_english`] || ""
        }
    }

    // Helper function to get explanation
    const getExplanation = (question, lang) => {
        switch (lang.toLowerCase()) {
            case "english":
                return question?.explanation_english || ""
            case "hindi":
                return question?.explanation_hindi || ""
            case "bengali":
                return question?.explanation_bengali || ""
            default:
                return question?.explanation_english || ""
        }
    }

    // Filter questions based on search term, status, subject, and handle multilingual search
    const filteredQuestions = testQuestions.filter((question) => {
        const questionText = getQuestionText(question, language)
        const subject = question?.subject?.id || ""
        const topic = question?.topic?._id || ""

        const matchesSearch =
            questionText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            topic?.toLowerCase().includes(searchTerm.toLowerCase())

        const matchesStatus = statusFilter === "" || question.status === statusFilter
        const matchesSubject = subjectFilter === "" || question.subject?._id === subjectFilter

        return matchesSearch && matchesStatus && matchesSubject
    })

    const truncateText = (text, maxLength = 80) => {
        if (!text) return ""
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        })
    }
    const [sequenceInputs, setSequenceInputs] = useState({});

    const handleQuestionSequenceChange = async (questionId, value) => {
        const newSequence = parseInt(value, 10);
        if (isNaN(newSequence) || newSequence < 1 || newSequence > testQuestions.length) return;

        const currentIndex = testQuestions.findIndex((q) => q._id === questionId);
        if (currentIndex === -1 || currentIndex + 1 === newSequence) return;

        try {
            const updatedList = [...testQuestions];
            const [movedQuestion] = updatedList.splice(currentIndex, 1);
            updatedList.splice(newSequence - 1, 0, movedQuestion);

            const resequenced = updatedList.map((q, index) => ({
                ...q,
                sequence: index + 1,
            }));

            setTestQuestions(resequenced);
            setSequenceInputs((prev) => ({ ...prev, [questionId]: "" }));

            await axios.post(`${baseUrl}/daily-mock-question-selected/update-sequence`, {
                questionId,
                testId: id,
                targetSequence: newSequence,
            }, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                    "Content-Type": "application/json",
                },
            });

            Swal.fire({
                title: "Success!",
                text: "Question sequence updated successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Failed to update sequence:", error);
            Swal.fire({
                title: "Error!",
                text: "Failed to update question order.",
                icon: "error",
            });
            fetchTestQuestions();
        }
    };
    if (loadingTest) {
        return (
            <section className="main-sec">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading test questions...</p>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    return (
        <>
            <style >{`
        .main-sec {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title {
          margin-bottom: 1.5rem;
        }

        .dash-head {
          color: #333;
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
        }

        .cards {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bus-filter {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .card-title {
          color: #333;
          font-weight: 700;
          margin: 0;
        }

        .btn-add-question {
          background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: white;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-add-question:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
          color: white;
        }

        .search-filter-container {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .search-input {
          flex: 1;
          min-width: 250px;
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          padding-left: 2.5rem;
          position: relative;
        }

        .search-container {
          position: relative;
          flex: 1;
          min-width: 250px;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .status-filter, .subject-filter, .language-filter {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          min-width: 150px;
        }

        .custom-table {
          border-radius: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .table {
          margin: 0;
        }

        .table thead th {
          background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border: none;
          padding: 1rem;
        }

        .table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f0f0f0;
        }

        .table tbody tr:hover {
          background-color: #f8f9ff;
        }

        .question-text {
          max-width: 400px;
          font-weight: 500;
          line-height: 1.4;
        }

        .options-preview {
          font-size: 0.85rem;
          color: #666;
          margin-top: 0.5rem;
        }

        .correct-answer {
          background: #d4edda;
          color: #155724;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 35px;
          height: 35px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .action-btn:hover {
          transform: translateY(-2px);
        }

        .action-btn:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }

        .edit-btn {
          background: #4facfe;
          color: white;
        }

        .delete-btn {
          background: #ef5350;
          color: white;
        }

        .view-btn {
          background: #26c6da;
          color: white;
        }

        .tooltip-text {
          visibility: hidden;
          width: 80px;
          background-color: #333;
          color: #fff;
          text-align: center;
          border-radius: 6px;
          padding: 5px;
          position: absolute;
          z-index: 1;
          bottom: 125%;
          left: 50%;
          margin-left: -40px;
          opacity: 0;
          transition: opacity 0.3s;
          font-size: 0.75rem;
        }

        .tooltip-text::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: #333 transparent transparent transparent;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state img {
          opacity: 0.7;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          color: #666;
          margin-bottom: 1rem;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #7436A9;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .subject-badge {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .topic-badge {
          background: #f3e5f5;
          color: #7b1fa2;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-top: 0.25rem;
          display: inline-block;
        }

        .language-indicator {
          background: #fff3cd;
          color: #856404;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .search-filter-container {
            flex-direction: column;
          }
          
          .search-container,
          .status-filter,
          .subject-filter,
          .language-filter {
            min-width: 100%;
          }
          
          .table-responsive {
            font-size: 0.85rem;
          }
          
          .question-text {
            max-width: 250px;
          }
        }
      `}</style>

            <section className="main-sec">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="dashboard-title">
                                <h4 className="dash-head">
                                    <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                                    Daily Mock Test Questions
                                </h4>
                            </div>
                            <div className="custom-bredcump">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item">
                                            <Link to="/">Dashboard</Link>
                                        </li>
                                        <li className="breadcrumb-item">
                                            <Link to="/free-mock-tests">Daily Mock Tests</Link>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            Daily Mock Test Questions
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                        <div className="col-lg-6 d-flex justify-content-end align-items-start">
                            <Link to={`/free-mock-tests/questions/assign/${id}`} className="btn-add-question me-2">
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Assign Questions
                            </Link>
                            <Link to="/free-mock-tests" className="btn-add-question" style={{ background: "#4facfe" }}>
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                Back to Tests
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="cards bus-list">
                                <div className="bus-filter">
                                    <div className="row align-items-center">
                                        <div className="col-lg-6">
                                            <h5 className="card-title">Daily Mock Test Questions List ({filteredQuestions.length})</h5>
                                        </div>
                                    </div>
                                </div>

                                <div className="search-filter-container">
                                    <div className="search-container">
                                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search questions, subjects, or topics..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <select
                                        className="status-filter"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    {subjects.length > 0 && (
                                        <select
                                            className="subject-filter"
                                            value={subjectFilter}
                                            onChange={(e) => setSubjectFilter(e.target.value)}
                                        >
                                            <option value="">All Subjects</option>
                                            {subjects.map((subject) => (
                                                <option key={subject?._id} value={subject?._id}>
                                                    {subject?.name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <select className="language-filter" value={language} onChange={(e) => setLanguage(e.target.value)}>
                                        <option value="english">English</option>
                                        <option value="hindi">Hindi</option>
                                        <option value="bengali">Bengali</option>
                                    </select>
                                </div>

                                <div className="table-responsive custom-table">
                                    <table className="table table-borderless">
                                        <thead>
                                            <tr>
                                                <th>SN</th>
                                                <th>Sequence</th>
                                                <th>Question</th>
                                                <th>Subject & Topic</th>
                                                <th>Correct Answer</th>
                                                <th>Created</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="8" className="loading-spinner">
                                                        <div className="spinner"></div>
                                                    </td>
                                                </tr>
                                            ) : filteredQuestions.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="empty-state">
                                                        <img src={empty || "/placeholder.svg"} alt="No questions" width="200px" />
                                                        <h4>No Questions found!</h4>
                                                        <p className="text-muted">
                                                            {searchTerm || statusFilter || subjectFilter
                                                                ? "Try adjusting your search or filter criteria"
                                                                : "This test has no questions assigned yet"}
                                                        </p>
                                                        <Link to={`/free-mock-tests/questions/assign/${id}`} className="btn-add-question mt-3">
                                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                            Assign Questions
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredQuestions.map((question, i) => (
                                                    <tr key={question?._id}>
                                                        <td>{i + 1}</td>
                                                        <td className="sequence-control-cell">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <input
                                                                    type="number"
                                                                    value={sequenceInputs[question._id] ?? question.sequence ?? i + 1}
                                                                    onChange={(e) =>
                                                                        setSequenceInputs((prev) => ({
                                                                            ...prev,
                                                                            [question._id]: e.target.value,
                                                                        }))
                                                                    }
                                                                    className="sequence-input"
                                                                    min={1}
                                                                    max={filteredQuestions.length}
                                                                    disabled={loading}
                                                                />
                                                                <button
                                                                    className="action-btn view-btn"
                                                                    onClick={() =>
                                                                        handleQuestionSequenceChange(question._id, sequenceInputs[question._id])
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="question-text">
                                                                {truncateText(getQuestionText(question, language), 100)}
                                                                {/* <div className="options-preview">
                                                                    A: {truncateText(getOptionText(question, language, "a"), 30)}
                                                                    <br />
                                                                    B: {truncateText(getOptionText(question, language, "b"), 30)}
                                                                </div> */}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <span className="subject-badge">{question?.subject?.name || "N/A"}</span>
                                                                {question?.topic && (
                                                                    <div className="topic-badge">{truncateText(question.topic?.name, 30)}</div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="correct-answer">Option {question?.correct_answer || "N/A"}</span>
                                                        </td>
                                                        <td>{formatDate(question?.createdAt)}</td>
                                                        <td>
                                                            <button
                                                                className={`status-badge ${question.status === "active" ? "status-active" : "status-inactive"
                                                                    }`}
                                                                onClick={() => handleStatus(question._id)}
                                                            >
                                                                {question?.status === "active" ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                        Active
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                                        Inactive
                                                                    </>
                                                                )}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                {/* <Link to={`/questions/view/${question?._id}`} className="action-btn view-btn">
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                    <span className="tooltip-text">View</span>
                                                                </Link> */}
                                                                <Link to={`/questions/edit/${question?._id}`} className="action-btn edit-btn">
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                    <span className="tooltip-text">Edit</span>
                                                                </Link>
                                                                <button
                                                                    className="action-btn delete-btn"
                                                                    onClick={() => handleRemoveQuestion(question?._id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                    <span className="tooltip-text">Remove</span>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
